/**
 * Admin Dashboard - FASE 2A Premium
 * Operatività quotidiana ottimizzata
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, RefreshCw, LogIn, LogOut,
  ChevronLeft, ChevronRight, FileText, Users,
  Bed, Brush, AlertCircle, TrendingUp, Phone,
  Check, X, Play, CreditCard, FileWarning,
  Clock, User, Home, Hash, Euro, TrendingDown,
  PiggyBank, Receipt, Wallet, ArrowRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function AdminDashboard() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [cleanings, setCleanings] = useState([]);
  const [cleaningStats, setCleaningStats] = useState({ pending: 0, in_progress: 0, completed: 0 });
  const [selectedCleaning, setSelectedCleaning] = useState(null);
  const [showCleaningDrawer, setShowCleaningDrawer] = useState(false);
  const [updatingCleaning, setUpdatingCleaning] = useState(false);
  const [weekData, setWeekData] = useState([]);
  const [weekStart, setWeekStart] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWeekDay, setSelectedWeekDay] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [units, setUnits] = useState([]);
  const [operationalAlerts, setOperationalAlerts] = useState([]);
  const [resolvingAlert, setResolvingAlert] = useState(null);
  
  // Financial data for dashboard
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(true);

  // Date utilities
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const realToday = new Date().toISOString().split('T')[0];
  const isToday = selectedDateStr === realToday;

  // Formattazione data compatta per mobile
  const formatDateHeader = () => {
    const day = selectedDate.toLocaleDateString('it-IT', { weekday: 'short' }).toUpperCase();
    const num = selectedDate.getDate();
    const month = selectedDate.toLocaleDateString('it-IT', { month: 'short' });
    return { day, num, month };
  };
  const dateHeader = formatDateHeader();

  // Calcola notti
  const calcNights = (arrivo, partenza) => {
    try {
      const a = new Date(arrivo);
      const p = new Date(partenza);
      return Math.ceil((p - a) / 86400000);
    } catch {
      return 0;
    }
  };

  // Fetch pulizie dal backend
  const fetchCleanings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/cleanings/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanings(res.data.tasks || []);
      setCleaningStats(res.data.stats || { pending: 0, in_progress: 0, completed: 0 });
    } catch (err) {
      console.error('Error fetching cleanings:', err);
    }
  }, [token]);

  // Fetch calendario settimanale
  const fetchWeekCleanings = useCallback(async (startDate) => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/cleanings/week`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate }
      });
      setWeekData(res.data.days || []);
      const today = new Date().toISOString().split('T')[0];
      const todayInWeek = res.data.days?.find(d => d.date === today);
      if (todayInWeek && !selectedWeekDay) {
        setSelectedWeekDay(todayInWeek);
      }
    } catch (err) {
      console.error('Error fetching week cleanings:', err);
    }
  }, [token, selectedWeekDay]);

  // Fetch dati finanziari per dashboard
  const fetchFinancialData = useCallback(async () => {
    if (!token) return;
    setLoadingFinancial(true);
    try {
      const res = await axios.get(`${API}/admin/financial/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialData(res.data);
    } catch (err) {
      console.error('Error fetching financial data:', err);
    } finally {
      setLoadingFinancial(false);
    }
  }, [token]);

  // Navigazione settimana
  const goToPreviousWeek = () => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() - 7);
    const newStart = current.toISOString().split('T')[0];
    setWeekStart(newStart);
    setSelectedWeekDay(null);
    fetchWeekCleanings(newStart);
  };

  const goToNextWeek = () => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + 7);
    const newStart = current.toISOString().split('T')[0];
    setWeekStart(newStart);
    setSelectedWeekDay(null);
    fetchWeekCleanings(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setSelectedDate(today);
    setWeekStart(todayStr);
    setSelectedWeekDay(null);
    fetchWeekCleanings(todayStr);
  };

  // Aggiorna stato pulizia
  const updateCleaningStatus = async (taskId, newStatus) => {
    setUpdatingCleaning(true);
    try {
      await axios.put(`${API}/admin/cleanings/${taskId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        newStatus === 'completed' ? 'Camera segnata come pulita!' : 
        newStatus === 'in_progress' ? 'Pulizia iniziata' : 
        newStatus === 'ready' ? 'Camera pronta per check-in!' : 'Stato aggiornato'
      );
      await fetchCleanings();
      await fetchWeekCleanings(weekStart);
      setShowCleaningDrawer(false);
      setSelectedCleaning(null);
    } catch (err) {
      console.error('Error updating cleaning:', err);
      toast.error('Errore aggiornamento stato');
    } finally {
      setUpdatingCleaning(false);
    }
  };

  // Filtra prenotazioni - SOLO OGGI
  const filterBySelectedDate = useCallback((bookings) => {
    const arr = bookings.filter(b => 
      b.data_arrivo === selectedDateStr && 
      b.status !== 'cancelled'
    );
    const dep = bookings.filter(b => 
      b.data_partenza === selectedDateStr && 
      b.status !== 'cancelled'
    );
    
    setArrivals(arr);
    setDepartures(dep);
  }, [selectedDateStr]);

  // Fetch dati
  const fetchAll = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const [statsRes, bookingsRes, unitsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/statistics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/alerts/entry-code-missing?resolved=false`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { alerts: [] } }))
      ]);
      setStats(statsRes.data);
      setUnits(unitsRes.data || []);
      setOperationalAlerts(alertsRes.data?.alerts || []);
      const bookings = bookingsRes.data || [];
      setAllBookings(bookings);
      filterBySelectedDate(bookings);
      await fetchCleanings();
      await fetchWeekCleanings(weekStart);
    } catch (err) {
      setError('Errore nel caricamento');
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token, filterBySelectedDate, fetchCleanings, fetchWeekCleanings, weekStart]);

  useEffect(() => {
    if (token && !authLoading) {
      fetchAll();
      fetchFinancialData();
    }
    else if (!authLoading) setLoading(false);
  }, [token, authLoading, fetchAll, fetchFinancialData]);

  useEffect(() => {
    if (allBookings.length > 0) filterBySelectedDate(allBookings);
  }, [selectedDate, allBookings, filterBySelectedDate]);

  // Navigazione data
  const navigateDate = (dir) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + dir);
    setSelectedDate(newDate);
  };

  // Gestione Alert
  const handleRetryAutomation = async (alert) => {
    try {
      setResolvingAlert(alert.id);
      const response = await axios.post(
        `${API}/admin/automation/retry-blocked/${alert.booking_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Automazione ripristinata: ${response.data.requeued} messaggi in coda`);
        const alertsRes = await axios.get(`${API}/admin/alerts/entry-code-missing?resolved=false`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setOperationalAlerts(alertsRes.data?.alerts || []);
      } else {
        toast.warning(response.data.message || 'Codice ancora mancante');
      }
    } catch (err) {
      toast.error('Errore durante il ripristino');
    } finally {
      setResolvingAlert(null);
    }
  };

  // Mini calendario 7 giorni
  const miniCalendar = useMemo(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date,
        dateStr,
        dayNum: date.getDate(),
        dayName: date.toLocaleDateString('it-IT', { weekday: 'short' }).substring(0, 2).toUpperCase(),
        isToday: i === 0,
        isSelected: dateStr === selectedDateStr,
        hasArrival: allBookings.some(b => b.data_arrivo === dateStr && b.status !== 'cancelled'),
        hasDeparture: allBookings.some(b => b.data_partenza === dateStr && b.status !== 'cancelled')
      });
    }
    return days;
  }, [allBookings, selectedDateStr]);

  // Contatori oggi
  const todayArrivals = arrivals.length;
  const todayDepartures = departures.length;

  // Helper per badge documenti
  const hasDocumentsMissing = (booking) => {
    return !booking.check_in_completed && !booking.checkin_online_completato;
  };

  // Helper per calcolo saldo
  const getSaldo = (booking) => {
    const totale = booking.prezzo_totale || 0;
    const acconto = booking.acconto_importo || 0;
    return totale - acconto;
  };

  // Helper per priorità pulizia
  const getPriorityLabel = (task) => {
    if (task.priority === 'critical') return { text: 'URGENTE', color: 'bg-rose-500 text-white' };
    if (task.priority === 'high') return { text: 'ALTA', color: 'bg-amber-500 text-white' };
    return { text: 'NORMALE', color: 'bg-slate-200 text-slate-700' };
  };

  // Helper per tipo pulizia
  const getCleaningTypeLabel = (task) => {
    if (task.type === 'checkout') return 'Cambio ospite';
    if (task.type === 'midstay') return `Pulizia soggiorno`;
    if (task.type === 'manual') return 'Pulizia manuale';
    return task.reason || 'Pulizia';
  };

  // Loading
  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Caricamento...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Errore
  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
          <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
          <p className="text-gray-700 mb-3">{error}</p>
          <button onClick={fetchAll} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            Riprova
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Container principale */}
      <div className="w-full min-h-screen bg-gray-50 overflow-x-hidden">
        
        {/* HEADER - Data e controlli */}
        <div className="bg-white border-b sticky top-0 z-20 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button onClick={() => navigateDate(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button onClick={() => navigateDate(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="text-center">
              <span className="text-[10px] text-gray-400 uppercase">{dateHeader.day}</span>
              <span className="text-lg font-bold text-gray-900 mx-1">{dateHeader.num}</span>
              <span className="text-xs text-gray-500">{dateHeader.month}</span>
            </div>
            
            <div className="flex items-center gap-1">
              {!isToday && (
                <button onClick={goToToday} className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded-full">
                  Oggi
                </button>
              )}
              <button onClick={fetchAll} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <RefreshCw className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* CONTENUTO */}
        <div className="p-3 space-y-3">
          
          {/* MINI CALENDARIO */}
          <div className="bg-white rounded-xl p-2 shadow-sm">
            <div className="flex gap-1 overflow-x-auto pb-1">
              {miniCalendar.map((day, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedDate(day.date)}
                  className={`flex-shrink-0 w-10 py-1.5 rounded-lg text-center transition-all ${
                    day.isSelected 
                      ? 'bg-blue-600 text-white' 
                      : day.isToday
                        ? 'bg-blue-50 text-blue-600 border border-blue-300'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  <p className={`text-[9px] ${day.isSelected ? 'text-blue-200' : 'text-gray-400'}`}>{day.dayName}</p>
                  <p className={`text-sm font-bold ${day.isSelected ? 'text-white' : 'text-gray-800'}`}>{day.dayNum}</p>
                  <div className="flex justify-center gap-0.5 mt-0.5">
                    {day.hasArrival && <span className="w-1 h-1 rounded-full bg-green-500"></span>}
                    {day.hasDeparture && <span className="w-1 h-1 rounded-full bg-orange-500"></span>}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-1.5 pt-1.5 border-t text-[9px] text-gray-400">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>Arrivo</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>Partenza</span>
            </div>
          </div>

          {/* SITUAZIONE OGGI */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-emerald-500 rounded-xl p-3 text-white">
              <p className="text-emerald-100 text-[10px]">Check-in</p>
              <p className="text-2xl font-bold">{todayArrivals}</p>
            </div>
            <div className="bg-amber-500 rounded-xl p-3 text-white">
              <p className="text-amber-100 text-[10px]">Check-out</p>
              <p className="text-2xl font-bold">{todayDepartures}</p>
            </div>
          </div>

          {/* RIGA 1: ARRIVI + PARTENZE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            
            {/* ARRIVI - Card Migliorata */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-3 py-2 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <LogIn className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Arrivi</h3>
                </div>
                <span className="text-sm font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{arrivals.length}</span>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                {arrivals.length === 0 ? (
                  <p className="p-4 text-center text-gray-400 text-xs">Nessun arrivo oggi</p>
                ) : (
                  arrivals.map((b, i) => (
                    <Link 
                      key={i} 
                      to={`/admin/bookings?id=${b.id}`} 
                      className="block px-3 py-2 hover:bg-emerald-50/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {/* Nome + Badge Documenti */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-gray-800 text-sm truncate">{b.nome_ospite}</p>
                            {hasDocumentsMissing(b) && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[9px] font-semibold rounded">
                                <FileWarning className="w-2.5 h-2.5" />
                                DOC
                              </span>
                            )}
                            {b.checkin_online_completato && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-semibold rounded">
                                <Check className="w-2.5 h-2.5" />
                                ONLINE
                              </span>
                            )}
                          </div>
                          {/* Camera + Canale + Notti */}
                          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500">
                            <span className="font-medium text-gray-700">{b.unit_nome}</span>
                            <span>•</span>
                            <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${
                              b.source === 'Booking.com' ? 'bg-blue-100 text-blue-700' :
                              b.source === 'Airbnb' ? 'bg-rose-100 text-rose-700' :
                              b.source === 'Vrbo' ? 'bg-indigo-100 text-indigo-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {b.source || 'Diretto'}
                            </span>
                            <span>•</span>
                            <span>{calcNights(b.data_arrivo, b.data_partenza)} notti</span>
                          </div>
                          {/* Telefono se presente */}
                          {b.telefono_ospite && (
                            <div className="flex items-center gap-1 mt-0.5 text-[10px] text-gray-400">
                              <Phone className="w-2.5 h-2.5" />
                              <span>{b.telefono_ospite}</span>
                            </div>
                          )}
                        </div>
                        {/* Orario check-in */}
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg">
                            {b.orario_checkin || '15:00'}
                          </span>
                          {b.stato_pagamento === 'pagato' && (
                            <div className="mt-1 text-[9px] text-emerald-600 font-medium">✓ Pagato</div>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>

            {/* PARTENZE - Card Migliorata */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center">
                    <LogOut className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Partenze</h3>
                </div>
                <span className="text-sm font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{departures.length}</span>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                {departures.length === 0 ? (
                  <p className="p-4 text-center text-gray-400 text-xs">Nessuna partenza oggi</p>
                ) : (
                  departures.map((b, i) => (
                    <Link 
                      key={i} 
                      to={`/admin/bookings?id=${b.id}`} 
                      className="block px-3 py-2 hover:bg-amber-50/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {/* Nome */}
                          <p className="font-semibold text-gray-800 text-sm truncate">{b.nome_ospite}</p>
                          {/* Camera + Canale */}
                          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500">
                            <span className="font-medium text-gray-700">{b.unit_nome}</span>
                            <span>•</span>
                            <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${
                              b.source === 'Booking.com' ? 'bg-blue-100 text-blue-700' :
                              b.source === 'Airbnb' ? 'bg-rose-100 text-rose-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {b.source || 'Diretto'}
                            </span>
                          </div>
                          {/* Stato Pagamento */}
                          <div className="flex items-center gap-2 mt-1">
                            {b.stato_pagamento === 'pagato' ? (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 font-medium">
                                <Check className="w-3 h-3" />
                                Saldato
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] text-rose-600 font-semibold">
                                <Euro className="w-3 h-3" />
                                Saldo: €{getSaldo(b).toLocaleString('it-IT')}
                              </span>
                            )}
                            {b.check_in_completed && (
                              <span className="text-[9px] text-gray-400">• Doc ✓</span>
                            )}
                          </div>
                        </div>
                        {/* Orario check-out */}
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg">
                            {b.orario_checkout || '10:00'}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* RIGA 2: CALENDARIO PULIZIE + NOTIFICHE */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            
            {/* CALENDARIO PULIZIE - 3 colonne */}
            <div className="md:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-slate-700 rounded-lg flex items-center justify-center">
                      <Brush className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">Pulizie</h3>
                    {cleaningStats.pending > 0 && (
                      <span className="text-[10px] font-bold text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded">
                        {cleaningStats.pending} da fare
                      </span>
                    )}
                  </div>
                  <Link to="/admin/cleaning-calendar" className="text-xs text-slate-600 hover:text-slate-800 font-medium">
                    Calendario →
                  </Link>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <button onClick={goToPreviousWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                    <ChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                  <button onClick={goToToday} className="text-xs text-slate-700 font-semibold px-3 py-1 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                    Oggi
                  </button>
                  <button onClick={goToNextWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>
              
              {/* Date in riga orizzontale con tooltip hover */}
              <div className="grid grid-cols-7 gap-1 p-3 bg-slate-50/50">
                {weekData.map((day) => (
                  <div key={day.date} className="relative">
                    <button
                      onClick={() => setSelectedWeekDay(day)}
                      onMouseEnter={() => setHoveredDay(day)}
                      onMouseLeave={() => setHoveredDay(null)}
                      className={`w-full p-2 rounded-xl text-center transition-all ${
                        selectedWeekDay?.date === day.date 
                          ? 'bg-slate-700 text-white shadow-md' 
                          : day.is_today 
                            ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-200' 
                            : 'bg-white hover:bg-slate-100 border border-slate-100'
                      }`}
                    >
                      <p className="text-[9px] font-medium opacity-70 uppercase">{day.day_name}</p>
                      <p className={`text-base font-bold ${selectedWeekDay?.date === day.date ? 'text-white' : ''}`}>
                        {day.day_number}
                      </p>
                      {day.total > 0 ? (
                        <div className={`mt-1 flex items-center justify-center gap-0.5`}>
                          <span className={`w-2 h-2 rounded-full ${
                            day.status === 'pending' ? 'bg-rose-400' :
                            day.status === 'in_progress' ? 'bg-amber-400' : 'bg-emerald-400'
                          }`}></span>
                          <span className={`text-[10px] font-medium ${
                            selectedWeekDay?.date === day.date ? 'text-white' :
                            day.status === 'pending' ? 'text-rose-600' :
                            day.status === 'in_progress' ? 'text-amber-600' : 'text-emerald-600'
                          }`}>{day.total}</span>
                        </div>
                      ) : (
                        <div className="mt-1 h-4"></div>
                      )}
                    </button>
                    
                    {/* Tooltip hover */}
                    {hoveredDay?.date === day.date && day.total > 0 && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-30 w-48 bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg pointer-events-none">
                        <p className="font-semibold mb-1">{day.total} pulizie</p>
                        {day.tasks.slice(0, 3).map((t, idx) => (
                          <p key={idx} className="text-slate-300 truncate">• {t.unit_name}</p>
                        ))}
                        {day.tasks.length > 3 && (
                          <p className="text-slate-400 mt-1">+{day.tasks.length - 3} altre</p>
                        )}
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-8 border-transparent border-t-slate-800"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Dettaglio giorno selezionato - Card Pulizie Complete */}
              <div className="max-h-52 overflow-y-auto">
                {selectedWeekDay ? (
                  selectedWeekDay.tasks.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-emerald-600 font-medium text-sm">✓ Nessuna pulizia</p>
                      <p className="text-gray-400 text-xs">{selectedWeekDay.day_name} {selectedWeekDay.day_number}</p>
                    </div>
                  ) : (
                    selectedWeekDay.tasks.map((task) => (
                      <div 
                        key={task.id} 
                        onClick={() => { setSelectedCleaning(task); setShowCleaningDrawer(true); }}
                        className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                          task.status === 'completed' || task.status === 'ready' ? 'bg-emerald-50/30' :
                          task.status === 'in_progress' ? 'bg-amber-50/30' :
                          task.priority === 'critical' ? 'bg-rose-50/30' : ''
                        } hover:bg-slate-50`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Riga 1: Camera */}
                            <div className="flex items-center gap-2">
                              <Home className="w-3.5 h-3.5 text-slate-500" />
                              <p className="font-semibold text-gray-800 text-sm">{task.unit_name}</p>
                            </div>
                            {/* Riga 2: Ospite */}
                            {task.guest_name && task.guest_name !== 'Ospite' && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <User className="w-3 h-3 text-slate-400" />
                                <p className="text-xs text-gray-600">{task.guest_name}</p>
                              </div>
                            )}
                            {/* Riga 3: Tipo + Stato */}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-[10px] text-gray-500">{getCleaningTypeLabel(task)}</span>
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                task.status === 'ready' ? 'bg-emerald-100 text-emerald-700' :
                                task.status === 'completed' ? 'bg-emerald-100 text-emerald-600' :
                                task.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                              }`}>
                                {task.status === 'ready' ? '✓ PRONTA' :
                                 task.status === 'completed' ? '✓ PULITA' :
                                 task.status === 'in_progress' ? 'IN CORSO' : 'DA FARE'}
                              </span>
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${getPriorityLabel(task).color}`}>
                                {getPriorityLabel(task).text}
                              </span>
                            </div>
                          </div>
                          {/* Colonna destra: Orari */}
                          <div className="text-right text-[10px] space-y-0.5 flex-shrink-0">
                            {task.checkout_time && (
                              <div className="text-gray-500">
                                <span className="text-gray-400">Out:</span> {task.checkout_time}
                              </div>
                            )}
                            {task.has_new_arrival && (
                              <div className="text-rose-600 font-semibold">
                                <span className="text-rose-400">In:</span> {task.next_checkin_time || '15:00'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  <p className="p-4 text-center text-gray-400 text-sm">Seleziona un giorno</p>
                )}
              </div>
            </div>

            {/* NOTIFICHE - 2 colonne */}
            <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-slate-600 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Notifiche</h3>
                </div>
                {operationalAlerts.length > 0 && (
                  <span className="text-xs font-bold text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">
                    {operationalAlerts.length}
                  </span>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto">
                {operationalAlerts.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-gray-600 text-sm font-medium">Tutto in ordine</p>
                    <p className="text-gray-400 text-xs">Nessun problema da risolvere</p>
                  </div>
                ) : (
                  operationalAlerts.map((alert) => (
                    <div key={alert.id} className="px-4 py-3 border-b border-gray-50 hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          alert.priority === 'critical' ? 'bg-rose-500' :
                          alert.priority === 'high' ? 'bg-amber-500' : 'bg-blue-500'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{alert.booking_ref || alert.guest_name}</p>
                          <p className="text-xs text-gray-500 truncate">{alert.message}</p>
                          <div className="mt-2 flex gap-2">
                            <Link 
                              to={`/admin/bookings?id=${alert.booking_id}`}
                              className="text-xs px-2.5 py-1 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                            >
                              Gestisci
                            </Link>
                            <button 
                              onClick={() => handleRetryAutomation(alert)}
                              disabled={resolvingAlert === alert.id}
                              className="text-xs px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-medium flex items-center gap-1"
                            >
                              {resolvingAlert === alert.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )}
                              Risolto
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* KPI BARRA */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats?.overview?.total_bookings_month || 0}</p>
                <p className="text-xs text-gray-500">Prenotazioni</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-emerald-500" />
                  <span className="text-[10px] text-emerald-600">mese</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats?.overview?.nights_sold_month || 0}</p>
                <p className="text-xs text-gray-500">Notti vendute</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className="text-[10px] text-gray-400">questo mese</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{Math.round(stats?.overview?.occupancy_rate || 0)}%</p>
                <p className="text-xs text-gray-500">Occupazione</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{width: `${Math.round(stats?.overview?.occupancy_rate || 0)}%`}}></div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">€{(stats?.overview?.revenue_month || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</p>
                <p className="text-xs text-gray-500">Ricavi</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-emerald-500" />
                  <span className="text-[10px] text-emerald-600">mese</span>
                </div>
              </div>
              
              <div className="text-center hidden md:block">
                <p className="text-2xl font-bold text-gray-900">€{stats?.overview?.nights_sold_month > 0 ? Math.round((stats?.overview?.revenue_month || 0) / stats.overview.nights_sold_month) : 0}</p>
                <p className="text-xs text-gray-500">ADR</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className="text-[10px] text-gray-400">media notte</span>
                </div>
              </div>
              
              <div className="text-center hidden md:block">
                <p className="text-2xl font-bold text-slate-700">€{Math.round(((stats?.overview?.revenue_month || 0) / 30) * ((stats?.overview?.occupancy_rate || 0) / 100))}</p>
                <p className="text-xs text-gray-500">RevPAR</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className="text-[10px] text-gray-400">per camera</span>
                </div>
              </div>
            </div>
          </div>

          {/* PANORAMICA FINANZIARIA - Stile Legacy */}
          {financialData && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-[#C5A059]/10 to-amber-50 border-b border-amber-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-[#C5A059] rounded-lg flex items-center justify-center">
                    <PiggyBank className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm">Panoramica Finanziaria {financialData.ytd?.anno || new Date().getFullYear()}</h3>
                  </div>
                </div>
                <Link to="/admin/financial" className="text-xs text-[#C5A059] hover:text-[#B08D4A] font-medium flex items-center gap-1">
                  Dettagli <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              
              <div className="p-4">
                {/* KPI YTD */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100">
                    <p className="text-[10px] text-green-600 uppercase font-medium">Entrate YTD</p>
                    <p className="text-xl font-bold text-green-700">
                      €{(financialData.ytd?.totale_lordo || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}
                    </p>
                    {financialData.ytd?.trend_vs_last_year && (
                      <div className={`flex items-center mt-1 text-[10px] ${financialData.ytd.trend_vs_last_year >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {financialData.ytd.trend_vs_last_year >= 0 ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
                        {financialData.ytd.trend_vs_last_year >= 0 ? '+' : ''}{financialData.ytd.trend_vs_last_year}%
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100">
                    <p className="text-[10px] text-blue-600 uppercase font-medium">Netto YTD</p>
                    <p className="text-xl font-bold text-blue-700">
                      €{(financialData.ytd?.totale_netto || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}
                    </p>
                    <p className="text-[9px] text-blue-500 mt-1">Dopo commissioni</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-3 border border-red-100">
                    <p className="text-[10px] text-red-600 uppercase font-medium">Spese YTD</p>
                    <p className="text-xl font-bold text-red-700">
                      €{(financialData.ytd?.totale_spese || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}
                    </p>
                    <p className="text-[9px] text-red-500 mt-1">Operative</p>
                  </div>
                  
                  <div className={`bg-gradient-to-br ${(financialData.ytd?.profitto_netto || 0) >= 0 ? 'from-amber-50 to-yellow-50 border-amber-100' : 'from-red-50 to-rose-50 border-red-100'} rounded-xl p-3 border`}>
                    <p className="text-[10px] text-[#C5A059] uppercase font-medium">Profitto YTD</p>
                    <p className={`text-xl font-bold ${(financialData.ytd?.profitto_netto || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      €{(financialData.ytd?.profitto_netto || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}
                    </p>
                    <p className="text-[9px] text-gray-500 mt-1">Netto - Spese</p>
                  </div>
                </div>
                
                {/* Mese Corrente - Compatto */}
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <h4 className="font-semibold text-gray-700 text-sm mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#C5A059]" />
                      {financialData.mese_corrente?.mese || 'Mese Corrente'}
                    </h4>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Lordo</span>
                        <span className="font-semibold">€{(financialData.mese_corrente?.totale_lordo || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>- Commissioni</span>
                        <span>-€{(financialData.mese_corrente?.totale_commissioni || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between text-orange-600">
                        <span>- Cedolare</span>
                        <span>-€{(financialData.mese_corrente?.cedolare_secca || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200">
                        <span className="text-blue-600 font-medium">= Netto</span>
                        <span className="font-bold text-blue-600">€{(financialData.mese_corrente?.totale_netto || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>- Spese</span>
                        <span>-€{(financialData.mese_corrente?.totale_spese || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between bg-[#C5A059]/10 px-2 py-1.5 rounded-lg mt-1">
                        <span className="font-bold text-[#C5A059]">= PROFITTO</span>
                        <span className={`font-bold ${(financialData.mese_corrente?.profitto || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          €{(financialData.mese_corrente?.profitto || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t border-gray-200">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-800">{financialData.mese_corrente?.num_prenotazioni || 0}</p>
                        <p className="text-[9px] text-gray-500">Prenotazioni</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-800">€{(financialData.mese_corrente?.prezzo_medio_notte || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})}</p>
                        <p className="text-[9px] text-gray-500">Media/notte</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Spese per Categoria */}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <h4 className="font-semibold text-gray-700 text-sm mb-2 flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-red-500" />
                      Spese per Categoria (YTD)
                    </h4>
                    <div className="space-y-1.5">
                      {financialData.spese_per_categoria && Object.entries(financialData.spese_per_categoria).length > 0 ? (
                        Object.entries(financialData.spese_per_categoria).slice(0, 5).map(([cat, importo]) => (
                          <div key={cat} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 capitalize">{cat}</span>
                            <span className="font-medium text-red-600">€{importo.toLocaleString('it-IT', {maximumFractionDigits: 0})}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-400 text-sm italic">Nessuna spesa registrata</p>
                      )}
                    </div>
                    <Link 
                      to="/admin/economic/spese" 
                      className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-[#C5A059] hover:text-[#B08D4A] font-medium py-1.5 border border-[#C5A059]/30 rounded-lg hover:bg-[#C5A059]/5 transition-colors"
                    >
                      Gestisci Spese <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AZIONI RAPIDE */}
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <h3 className="font-semibold text-gray-800 text-sm mb-2">Azioni Rapide</h3>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/admin/quotes/new" className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-xs">Preventivo</p>
                  <p className="text-[9px] text-gray-500">Nuovo</p>
                </div>
              </Link>
              <Link to="/admin/bookings" className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-xs">Prenotazioni</p>
                  <p className="text-[9px] text-gray-500">Gestisci</p>
                </div>
              </Link>
              <Link to="/admin/checkins" className="flex items-center gap-2 p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-xs">Check-in</p>
                  <p className="text-[9px] text-gray-500">Schedine</p>
                </div>
              </Link>
              <Link to="/admin/revenue-intelligence" className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-xs">Revenue AI</p>
                  <p className="text-[9px] text-gray-500">Ottimizza</p>
                </div>
              </Link>
            </div>
          </div>

          {/* STRUTTURE */}
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <h3 className="font-semibold text-gray-800 text-sm mb-2">Strutture</h3>
            <div className="space-y-2">
              {units.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">Nessuna struttura</p>
              ) : (
                units.map((unit, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                      <Bed className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-xs truncate">{unit.nome}</p>
                      <p className="text-[9px] text-gray-500">Max {unit.capacita_max} ospiti</p>
                    </div>
                    <p className="text-sm font-bold text-amber-600">€{unit.prezzo_base}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Spazio per bottom nav mobile */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* DRAWER LATERALE PULIZIA */}
      {showCleaningDrawer && selectedCleaning && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/40 z-40 transition-opacity"
            onClick={() => { setShowCleaningDrawer(false); setSelectedCleaning(null); }}
          />
          
          {/* Drawer */}
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header Drawer */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-xs font-medium uppercase tracking-wide">Pulizia</p>
                  <h2 className="font-bold text-xl">{selectedCleaning.unit_name}</h2>
                </div>
                <button 
                  onClick={() => { setShowCleaningDrawer(false); setSelectedCleaning(null); }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Status badge */}
              <div className="mt-3 flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedCleaning.status === 'ready' ? 'bg-emerald-500 text-white' :
                  selectedCleaning.status === 'completed' ? 'bg-emerald-400 text-white' :
                  selectedCleaning.status === 'in_progress' ? 'bg-amber-500 text-white' : 'bg-rose-500 text-white'
                }`}>
                  {selectedCleaning.status === 'ready' ? '✓ Pronta' :
                   selectedCleaning.status === 'completed' ? '✓ Pulita' :
                   selectedCleaning.status === 'in_progress' ? 'In corso' : 'Da fare'}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${getPriorityLabel(selectedCleaning).color}`}>
                  {getPriorityLabel(selectedCleaning).text}
                </span>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Ospite */}
              {selectedCleaning.guest_name && selectedCleaning.guest_name !== 'Ospite' && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ospite</p>
                      <p className="font-semibold text-gray-800">{selectedCleaning.guest_name}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Dettagli griglia */}
              <div className="grid grid-cols-2 gap-3">
                {/* Motivo */}
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-1">Motivo</p>
                  <p className="font-semibold text-gray-800 text-sm">{getCleaningTypeLabel(selectedCleaning)}</p>
                </div>
                
                {/* Tipo */}
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-1">Tipo</p>
                  <p className="font-semibold text-gray-800 text-sm capitalize">{selectedCleaning.type || 'Standard'}</p>
                </div>
                
                {/* Check-out */}
                {selectedCleaning.checkout_time && (
                  <div className="bg-amber-50 rounded-xl p-3">
                    <p className="text-xs text-amber-600 mb-1">Check-out</p>
                    <p className="font-bold text-amber-700">{selectedCleaning.checkout_time}</p>
                  </div>
                )}
                
                {/* Prossimo Check-in */}
                {selectedCleaning.has_new_arrival && (
                  <div className="bg-rose-50 rounded-xl p-3 border border-rose-200">
                    <p className="text-xs text-rose-600 mb-1">Check-in</p>
                    <p className="font-bold text-rose-700">{selectedCleaning.next_checkin_time || '15:00'}</p>
                  </div>
                )}
              </div>
              
              {/* Prossimo ospite */}
              {selectedCleaning.has_new_arrival && selectedCleaning.next_guest && (
                <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                  <div className="flex items-center gap-2 text-rose-700 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <p className="font-semibold text-sm">Prossimo ospite in arrivo</p>
                  </div>
                  <p className="text-rose-800 font-medium">{selectedCleaning.next_guest}</p>
                  <p className="text-rose-600 text-xs mt-1">Camera richiesta urgentemente</p>
                </div>
              )}
              
              {/* Note */}
              {selectedCleaning.notes && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Note</p>
                  <p className="text-gray-700 text-sm">{selectedCleaning.notes}</p>
                </div>
              )}
              
              {/* Info completamento */}
              {(selectedCleaning.status === 'completed' || selectedCleaning.status === 'ready') && selectedCleaning.completed_at && (
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-xs text-emerald-600 mb-1">Completata</p>
                  <p className="text-emerald-700 text-sm font-medium">
                    {new Date(selectedCleaning.completed_at).toLocaleString('it-IT')}
                  </p>
                  {selectedCleaning.completed_by && (
                    <p className="text-emerald-600 text-xs mt-1">da {selectedCleaning.completed_by}</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Actions Footer */}
            <div className="border-t bg-white p-4 space-y-2">
              {selectedCleaning.status === 'pending' && (
                <>
                  <Button
                    onClick={() => updateCleaningStatus(selectedCleaning.id, 'in_progress')}
                    disabled={updatingCleaning}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3"
                  >
                    {updatingCleaning ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Play className="w-5 h-5 mr-2" />
                    )}
                    Inizia Pulizia
                  </Button>
                  <Button
                    onClick={() => updateCleaningStatus(selectedCleaning.id, 'ready')}
                    disabled={updatingCleaning}
                    variant="outline"
                    className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-50 py-3"
                  >
                    {updatingCleaning ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Check className="w-5 h-5 mr-2" />
                    )}
                    Segna come Pronta
                  </Button>
                </>
              )}
              
              {selectedCleaning.status === 'in_progress' && (
                <Button
                  onClick={() => updateCleaningStatus(selectedCleaning.id, 'ready')}
                  disabled={updatingCleaning}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg font-bold"
                >
                  {updatingCleaning ? (
                    <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                  ) : (
                    <Check className="w-6 h-6 mr-2" />
                  )}
                  CAMERA PRONTA
                </Button>
              )}
              
              {(selectedCleaning.status === 'completed' || selectedCleaning.status === 'ready') && (
                <div className="text-center py-3">
                  <div className="inline-flex items-center gap-2 text-emerald-600 font-medium">
                    <Check className="w-5 h-5" />
                    {selectedCleaning.status === 'ready' ? 'Camera pronta per il check-in' : 'Pulizia completata'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
